import React, { useState } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Wallet, CheckCircle2, Loader2, ArrowLeft, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRecordPayment, useClientSelf } from '@/hooks/use-api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
export function ClientPayment() {
  const [amount, setAmount] = useState('');
  const [success, setSuccess] = useState(false);
  const { data: client, isLoading: clientLoading } = useClientSelf();
  const recordPayment = useRecordPayment();
  const navigate = useNavigate();
  const handlePay = async () => {
    if (!amount || !client?.id) {
      toast.error("Required data missing. Please try again.");
      return;
    }
    try {
      await recordPayment.mutateAsync({
        clientId: client.id,
        amount: parseFloat(amount),
        type: 'Collection',
        officerId: 'self-portal',
        actorName: client.name || 'Member'
      });
      const currentBalance = client?.balance ?? 0;
      const phone = client?.phone ?? 'Unknown';
      console.log(`[SMS STUB] To: ${phone} | Msg: Perfect Match Receipt. GHS ${amount} received for Susu. New Balance: GHS ${(currentBalance + parseFloat(amount)).toFixed(2)}.`);
      setSuccess(true);
      toast.success("Payment request initiated!");
    } catch (err) {
      toast.error("Failed to process request. Please try again.");
    }
  };
  return (
    <AppLayout container className="bg-accent/20">
      <div className="max-w-xl mx-auto space-y-8">
        {!success ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="flex items-center gap-4 mb-6">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate(-1)} 
                disabled={recordPayment.isPending}
                className="text-muted-foreground hover:bg-muted/50"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-2xl font-black text-foreground uppercase tracking-tight">Add to Savings</h1>
            </div>
            <Card className="border-none shadow-glass rounded-3xl overflow-hidden bg-card">
              <CardHeader className="bg-primary text-primary-foreground p-8">
                <CardTitle className="flex items-center gap-2 font-black uppercase tracking-tight">
                  <Wallet className="h-6 w-6" />
                  Susu Contribution
                </CardTitle>
                <CardDescription className="text-primary-foreground/80 font-bold uppercase tracking-widest text-[10px]">
                  Record a daily contribution to your Susu account.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-8 space-y-8">
                <div className="space-y-4">
                  <Label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest px-1">Select Amount (GHS)</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {[10, 20, 50, 100, 200, 500].map(val => (
                      <Button
                        key={val}
                        variant={amount === val.toString() ? "default" : "outline"}
                        className={cn(
                          "h-14 font-bold rounded-xl border-border",
                          amount === val.toString() ? "bg-primary hover:bg-primary/90 text-primary-foreground" : "hover:bg-muted/50 text-muted-foreground"
                        )}
                        onClick={() => setAmount(val.toString())}
                        disabled={clientLoading || recordPayment.isPending}
                      >
                        {val}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="custom" className="text-[10px] font-black text-muted-foreground uppercase tracking-widest px-1">Or Custom Amount</Label>
                  <Input
                    id="custom"
                    type="number"
                    placeholder="0.00"
                    className="h-16 text-2xl font-mono font-bold text-center rounded-2xl bg-muted/50 border-border focus-visible:ring-primary text-foreground"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    disabled={clientLoading || recordPayment.isPending}
                  />
                </div>
                <div className="bg-muted/50 p-4 rounded-2xl border border-border flex gap-3">
                  <div className="h-8 w-8 rounded-full bg-muted/80 flex items-center justify-center shrink-0">
                    <Send className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-[10px] text-muted-foreground font-bold leading-tight uppercase tracking-tight">
                    By submitting, you are requesting your field officer to verify a physical cash pickup of GHS {amount || '0'}. An SMS receipt will be sent upon verification.
                  </p>
                </div>
                <Button
                  className="w-full h-16 text-lg font-black bg-primary hover:bg-primary/90 rounded-2xl shadow-lg shadow-primary/20 uppercase tracking-tight"
                  onClick={handlePay}
                  disabled={!amount || clientLoading || recordPayment.isPending}
                >
                  {recordPayment.isPending ? <Loader2 className="mr-2 h-6 w-6 animate-spin" /> : null}
                  Initiate Payment Record
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-6 py-12"
          >
            <div className="mx-auto h-24 w-24 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <CheckCircle2 className="h-14 w-14 text-emerald-500" />
            </div>
            <div className="space-y-2">
              <h2 className="text-3xl font-black text-foreground uppercase tracking-tight">Request Sent!</h2>
              <p className="text-muted-foreground font-bold uppercase tracking-widest text-xs">
                GHS {amount} has been logged. Please ensure cash is ready for collection.
              </p>
            </div>
            <Button
              variant="outline"
              className="h-12 px-8 rounded-xl font-black text-muted-foreground border-border hover:bg-muted/50"
              onClick={() => navigate('/client/dashboard')}
            >
              Back to Dashboard
            </Button>
          </motion.div>
        )}
      </div>
    </AppLayout>
  );
}